/**
 * Mapa choropleth Perú con drill-down departamento → provincia → distrito.
 * Estética institucional inspirada en diseño Anthropic Design Bundle.
 * Datos reales del portal de transparencia (placements/spending por ubigeo).
 *
 * GeoJSON properties:
 *   dep:  FIRST_IDDP (2 dig), NOMBDEP
 *   prov: FIRST_IDPR (4 dig), NOMBPROV
 *   dist: IDDIST (6 dig), NOMBDIST
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { geoMercator, geoPath, type GeoPermissibleObjects } from "d3-geo";
import { scaleQuantile } from "d3-scale";
import { zoom as d3zoom, zoomIdentity, type ZoomBehavior } from "d3-zoom";
import { select } from "d3-selection";
import "d3-transition"; // side-effect: añade .transition() a Selection
import { ChevronRight, Plus, Minus, RotateCcw } from "lucide-react";
import { media, type PortalFilters } from "@/lib/api";

const RAMP = ["#e8f1fb", "#c3dbf0", "#8fbce0", "#559bcb", "#2c73ac", "#0b477e"];

const ORDER: Record<Level, number> = { dep: 0, prov: 1, dist: 2 };

type GeoFeature = {
  type: "Feature";
  geometry: GeoJSON.Geometry;
  properties: Record<string, unknown>;
};
type GeoCollection = { type: "FeatureCollection"; features: GeoFeature[] };

type DataItem = { ubigeo: string; name: string; placements: number; spending: number };

type Level = "dep" | "prov" | "dist";

export interface PeruAudienceMapProps {
  token: string;
  filters: PortalFilters;
  /** Datos iniciales departamentales (del portal payload — evita primer fetch). */
  initialDep: Array<{ ubigeo: string; name: string; placements: number; spending: number }>;
}

function depCode(p: Record<string, unknown>): string {
  return ((p.FIRST_IDDP || p.IDDPTO || p.CCDD || "") as string).toString().padStart(2, "0");
}
function depName(p: Record<string, unknown>): string {
  return ((p.NOMBDEP || p.NOMDEP || p.name || "") as string).toString();
}
function provCode(p: Record<string, unknown>): string {
  return ((p.FIRST_IDPR || p.IDPROV || p.CCPP || "") as string).toString().padStart(4, "0");
}
function provName(p: Record<string, unknown>): string {
  return ((p.NOMBPROV || p.PROVINCIA || p.name || "") as string).toString();
}
function distCode(p: Record<string, unknown>): string {
  return ((p.IDDIST || p.UBIGEO || p.CCDI || "") as string).toString().padStart(6, "0");
}
function distName(p: Record<string, unknown>): string {
  return ((p.NOMBDIST || p.DISTRITO || p.name || "") as string).toString();
}

function titleCase(s: string): string {
  return s.toLowerCase().replace(/(^|[\s\-/])([a-záéíóúñ])/g, (_, a, b) => a + b.toUpperCase());
}

function fmtN(n: number): string {
  return n.toLocaleString("es-PE", { maximumFractionDigits: 0 });
}

export function PeruAudienceMap({ token, filters, initialDep }: PeruAudienceMapProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const gZoomRef = useRef<SVGGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const zoomBehaviorRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);

  // GeoJSON layers
  const [depGeo, setDepGeo] = useState<GeoCollection | null>(null);
  const [provGeo, setProvGeo] = useState<GeoCollection | null>(null);
  const [distGeo, setDistGeo] = useState<GeoCollection | null>(null);
  const [geoLoading, setGeoLoading] = useState(true);

  // Estado navegación
  const [view, setView] = useState<{ level: Level; depCode: string | null; provCode: string | null }>({
    level: "dep", depCode: null, provCode: null,
  });
  // Vista previa para detectar dirección de navegación (drill-in vs drill-out) y animar el zoom.
  const prevViewRef = useRef(view);

  // Data por nivel (dep ya viene del payload; prov/dist se fetchean)
  const [depData, setDepData] = useState<DataItem[]>(initialDep);
  const [provData, setProvData] = useState<DataItem[]>([]);
  const [distData, setDistData] = useState<DataItem[]>([]);

  // Size
  const [size, setSize] = useState({ w: 800, h: 560 });

  // Hover
  const [hover, setHover] = useState<{ name: string; kind: string; value: number; subtitle: string } | null>(null);

  // Carga GeoJSON lazy
  useEffect(() => {
    let cancelled = false;
    setGeoLoading(true);
    Promise.all([
      fetch("/geo/peru_departamental_simple.geojson").then(r => r.json()),
      fetch("/geo/peru_provincial_simple.geojson").then(r => r.json()).catch(() => null),
      fetch("/geo/peru_distrital_simple.geojson").then(r => r.json()).catch(() => null),
    ]).then(([dep, prov, dist]) => {
      if (cancelled) return;
      setDepGeo(dep);
      setProvGeo(prov);
      setDistGeo(dist);
      setGeoLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  // Sync initialDep
  useEffect(() => { setDepData(initialDep); }, [initialDep]);

  // Resize observer
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(() => {
      setSize({ w: el.clientWidth, h: Math.max(420, el.clientHeight) });
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Features que definen el encuadre del nivel actual: al entrar a un departamento
  // proyectamos SOLO ese departamento (sus provincias llenan el marco), y al entrar
  // a una provincia proyectamos esa provincia (sus distritos llenan el marco).
  const fitFeatures = useMemo<GeoCollection | null>(() => {
    if (!depGeo) return null;
    if (view.level === "dist") {
      // Encuadra la provincia; si falta el geo provincial, cae al departamento
      // (en vez de a todo el país) para no descuadrar el mapa.
      if (view.provCode && provGeo) {
        const f = provGeo.features.find(x => provCode(x.properties) === view.provCode);
        if (f) return { type: "FeatureCollection", features: [f] };
      }
      if (view.depCode) {
        const f = depGeo.features.find(x => depCode(x.properties) === view.depCode);
        if (f) return { type: "FeatureCollection", features: [f] };
      }
    }
    if (view.level === "prov" && view.depCode) {
      const f = depGeo.features.find(x => depCode(x.properties) === view.depCode);
      if (f) return { type: "FeatureCollection", features: [f] };
    }
    return depGeo; // nivel nacional o fallback
  }, [depGeo, provGeo, view]);

  // Projection + path: reproyecta al encuadre del nivel → el scope llena el mapa.
  const pathGen = useMemo(() => {
    if (!fitFeatures) return null;
    const proj = geoMercator().fitExtent([[20, 20], [size.w - 20, size.h - 20]], fitFeatures as unknown as GeoPermissibleObjects);
    return geoPath(proj);
  }, [fitFeatures, size.w, size.h]);

  // Setup zoom behavior
  useEffect(() => {
    if (!svgRef.current || !gZoomRef.current) return;
    const svg = select(svgRef.current);
    const g = select(gZoomRef.current);
    const z = d3zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.2, 36])
      .on("zoom", (e) => {
        g.attr("transform", e.transform.toString());
      });
    zoomBehaviorRef.current = z;
    svg.call(z).on("dblclick.zoom", null);
  }, [size.w, size.h]);

  // Animación de zoom al cambiar de nivel. Como reproyectamos por scope, el nivel
  // ya llena el marco; aquí solo arrancamos desde un transform "alejado" (drill-in)
  // o "acercado" (drill-out) y transicionamos a identidad → sensación de zoom.
  useEffect(() => {
    const svg = svgRef.current, z = zoomBehaviorRef.current;
    if (!svg || !z || !pathGen) return;
    const prev = prevViewRef.current;
    prevViewRef.current = view;
    const delta = ORDER[view.level] - ORDER[prev.level];
    if (delta === 0) return; // mismo nivel (resize/filtros): sin animación

    // Pivote del zoom: la zona involucrada en la navegación, proyectada con el
    // encuadre NUEVO. Drill-in → el feature al que entramos (el scope); drill-out
    // → el hijo del que salimos. Así el zoom "nace"/"vuelve" desde esa zona.
    let anchorF: GeoFeature | undefined;
    if (delta > 0) {
      if (view.level === "prov" && view.depCode) anchorF = depGeo?.features.find(f => depCode(f.properties) === view.depCode);
      else if (view.level === "dist" && view.provCode) anchorF = provGeo?.features.find(f => provCode(f.properties) === view.provCode);
    } else {
      if (prev.level === "dist" && prev.provCode) anchorF = provGeo?.features.find(f => provCode(f.properties) === prev.provCode);
      else if (prev.level === "prov" && prev.depCode) anchorF = depGeo?.features.find(f => depCode(f.properties) === prev.depCode);
    }
    let ax = size.w / 2, ay = size.h / 2;
    if (anchorF) {
      const c = pathGen.centroid(anchorF as unknown as GeoPermissibleObjects);
      if (c && Number.isFinite(c[0]) && Number.isFinite(c[1])) { ax = c[0]; ay = c[1]; }
    }
    const startK = delta > 0 ? 0.32 : 2.6; // drill-in: alejado→llena ; drill-out: acercado→encoge
    const startT = zoomIdentity.translate(ax, ay).scale(startK).translate(-ax, -ay);
    const sel = select(svg);
    sel.interrupt().call(z.transform, startT);
    sel.transition().duration(700).call(z.transform, zoomIdentity);
  }, [view, pathGen, depGeo, provGeo, size.w, size.h]);

  const [loadingProv, setLoadingProv] = useState(false);
  const [loadingDist, setLoadingDist] = useState(false);

  // Fetch prov data al entrar a un departamento o cambiar filters
  useEffect(() => {
    if ((view.level === "prov" || view.level === "dist") && view.depCode) {
      setLoadingProv(true);
      media.getPublicGeoDetail(token, "prov", view.depCode, filters)
        .then(r => setProvData(r.items))
        .catch(() => setProvData([]))
        .finally(() => setLoadingProv(false));
    } else if (view.level === "dep") {
      setProvData([]);
      setDistData([]);
    }
  }, [view.level, view.depCode, token, filters]);

  // Fetch dist data al entrar a una provincia o cambiar filters
  useEffect(() => {
    if (view.level === "dist" && view.provCode) {
      setLoadingDist(true);
      media.getPublicGeoDetail(token, "dist", view.provCode, filters)
        .then(r => setDistData(r.items))
        .catch(() => setDistData([]))
        .finally(() => setLoadingDist(false));
    } else if (view.level !== "dist") {
      setDistData([]);
    }
  }, [view.level, view.provCode, token, filters]);

  // Quantile color scale por nivel
  const currentData = view.level === "dep" ? depData : view.level === "prov" ? provData : distData;
  const colorScale = useMemo(() => {
    const vals = currentData.map(d => d.placements).filter(v => v > 0);
    if (!vals.length) return null;
    return scaleQuantile<string>().domain(vals).range(RAMP);
  }, [currentData]);

  const dataByUbigeo = useMemo(() => {
    const m = new Map<string, DataItem>();
    for (const d of currentData) m.set(d.ubigeo, d);
    return m;
  }, [currentData]);

  // Current features filtradas para el nivel
  const visibleFeatures = useMemo(() => {
    if (!depGeo) return [];
    if (view.level === "dep") return depGeo.features;
    if (view.level === "prov" && view.depCode && provGeo) {
      return provGeo.features.filter(f => provCode(f.properties).startsWith(view.depCode!));
    }
    if (view.level === "dist" && view.provCode && distGeo) {
      return distGeo.features.filter(f => distCode(f.properties).startsWith(view.provCode!));
    }
    return [];
  }, [depGeo, provGeo, distGeo, view]);

  // Contorno del scope: a nivel nacional, todos los departamentos; dentro de un
  // departamento, su borde; dentro de una provincia, el borde de la provincia.
  const scopeBorders = useMemo<GeoFeature[]>(() => {
    if (view.level === "dep") return depGeo?.features ?? [];
    if (view.level === "prov") return depGeo?.features.filter(f => depCode(f.properties) === view.depCode) ?? [];
    return provGeo?.features.filter(f => provCode(f.properties) === view.provCode) ?? [];
  }, [depGeo, provGeo, view]);

  // Click → drill-down. La reproyección por scope + el efecto de animación
  // se encargan de "ampliar" el nivel a tamaño de mapa completo.
  function clickFeature(level: Level, code: string) {
    if (level === "dep") {
      setView({ level: "prov", depCode: code, provCode: null });
    } else if (level === "prov") {
      setView({ level: "dist", depCode: code.slice(0, 2), provCode: code });
    }
  }

  function resetToCountry() {
    setView({ level: "dep", depCode: null, provCode: null });
  }

  function backOneLevel() {
    if (view.level === "dist" && view.depCode) {
      setView({ level: "prov", depCode: view.depCode, provCode: null });
    } else {
      resetToCountry();
    }
  }

  function showTooltip(e: React.MouseEvent, item: DataItem | null, levelLabel: string) {
    if (!item || !tooltipRef.current) return;
    const tip = tooltipRef.current;
    tip.style.opacity = "1";
    tip.style.transform = "none";
    const pad = 14;
    const x = e.clientX + pad;
    const y = e.clientY + pad;
    tip.style.left = x + "px";
    tip.style.top = y + "px";
    setHover({
      name: titleCase(item.name),
      kind: levelLabel,
      value: item.placements,
      subtitle: `${fmtN(item.placements)} inserciones · PEN ${fmtN(item.spending)}`,
    });
  }
  function hideTooltip() {
    if (tooltipRef.current) tooltipRef.current.style.opacity = "0";
    setHover(null);
  }

  // KPIs panel
  const total = currentData.reduce((acc, d) => acc + d.placements, 0);
  const sumSpending = currentData.reduce((acc, d) => acc + d.spending, 0);
  const avg = currentData.length ? total / currentData.length : 0;
  const top = currentData.slice().sort((a, b) => b.placements - a.placements)[0];

  const levelTitle = view.level === "dep"
    ? "Perú"
    : view.level === "prov" && view.depCode
      ? titleCase(depGeo?.features.find(f => depCode(f.properties) === view.depCode)?.properties.NOMBDEP as string || "")
      : view.level === "dist" && view.provCode
        ? titleCase(provGeo?.features.find(f => provCode(f.properties) === view.provCode)?.properties.NOMBPROV as string || "")
        : "—";

  const levelKicker = view.level === "dep" ? "NIVEL · NACIONAL" : view.level === "prov" ? "NIVEL · DEPARTAMENTO" : "NIVEL · PROVINCIA";
  const levelScope = view.level === "dep"
    ? `${visibleFeatures.length} departamentos`
    : view.level === "prov" ? `${visibleFeatures.length} provincias` : `${visibleFeatures.length} distritos`;
  const rankTitle = view.level === "dep" ? "Departamentos" : view.level === "prov" ? "Provincias" : "Distritos";

  // Legend quantiles
  const legendRows = useMemo(() => {
    if (!colorScale || !currentData.length) return [];
    const q = colorScale.quantiles();
    const vals = currentData.map(d => d.placements);
    const min = Math.min(...vals), max = Math.max(...vals);
    const edges = [min, ...q, max];
    const counts = RAMP.map(() => 0);
    for (const d of currentData) {
      const c = colorScale(d.placements);
      const idx = RAMP.indexOf(c);
      if (idx >= 0) counts[idx]++;
    }
    return RAMP.map((color, i) => ({
      color,
      lo: edges[i] ?? 0,
      hi: edges[i + 1] ?? 0,
      count: counts[i],
    })).reverse();
  }, [colorScale, currentData]);

  // Breadcrumbs
  const crumbs: Array<{ label: string; lvl: Level }> = [{ label: "Perú", lvl: "dep" }];
  if (view.depCode) {
    const dn = titleCase(depGeo?.features.find(f => depCode(f.properties) === view.depCode)?.properties.NOMBDEP as string || "");
    crumbs.push({ label: dn, lvl: "prov" });
  }
  if (view.provCode) {
    const pn = titleCase(provGeo?.features.find(f => provCode(f.properties) === view.provCode)?.properties.NOMBPROV as string || "");
    crumbs.push({ label: pn, lvl: "dist" });
  }

  return (
    <div className="bg-white border border-ink-200 rounded-lg overflow-hidden">
      {/* Top institutional rule */}
      <div className="h-1 bg-[#c8102e]" />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] min-h-[560px]">
        {/* MAP STAGE */}
        <div ref={containerRef} className="relative bg-gradient-to-br from-[#f7f9fc] to-[#eef1f6] min-h-[560px] overflow-hidden">
          {/* Floating tools */}
          <div className="absolute left-3 top-3 z-10 flex flex-col gap-2">
            <nav className="flex items-center gap-1 flex-wrap bg-white/95 backdrop-blur border border-ink-200 rounded-lg px-2 py-1 shadow-sm">
              {crumbs.map((c, i) => (
                <span key={i} className="inline-flex items-center gap-1">
                  {i > 0 && <ChevronRight size={10} className="text-ink-400" />}
                  <button
                    type="button"
                    disabled={i === crumbs.length - 1}
                    onClick={() => {
                      if (i === 0) resetToCountry();
                      else if (i === 1 && view.depCode) {
                        setView({ level: "prov", depCode: view.depCode, provCode: null });
                      }
                    }}
                    className="px-1.5 py-0.5 text-xs font-semibold text-ink-700 hover:text-[#c8102e] disabled:text-ink-900 disabled:cursor-default"
                  >
                    {c.label}
                  </button>
                </span>
              ))}
            </nav>
            <div className="flex gap-1">
              <button onClick={() => svgRef.current && zoomBehaviorRef.current && select(svgRef.current).transition().call(zoomBehaviorRef.current.scaleBy, 1.6)}
                className="w-8 h-8 rounded-lg border border-ink-200 bg-white/95 hover:border-ink-700 flex items-center justify-center shadow-sm">
                <Plus size={14} />
              </button>
              <button onClick={() => svgRef.current && zoomBehaviorRef.current && select(svgRef.current).transition().call(zoomBehaviorRef.current.scaleBy, 1 / 1.6)}
                className="w-8 h-8 rounded-lg border border-ink-200 bg-white/95 hover:border-ink-700 flex items-center justify-center shadow-sm">
                <Minus size={14} />
              </button>
              <button onClick={resetToCountry}
                className="h-8 px-3 text-xs font-semibold rounded-lg border border-ink-200 bg-white/95 hover:border-ink-700 flex items-center gap-1 shadow-sm">
                <RotateCcw size={12} /> Perú
              </button>
              {view.level !== "dep" && (
                <button onClick={backOneLevel}
                  className="h-8 px-3 text-xs font-semibold rounded-lg border border-ink-200 bg-white/95 hover:border-ink-700 shadow-sm">
                  ← Atrás
                </button>
              )}
            </div>
          </div>

          {geoLoading ? (
            <div className="absolute inset-0 flex items-center justify-center text-ink-500 text-sm">
              Cargando geometría INEI…
            </div>
          ) : (
            <svg ref={svgRef} viewBox={`0 0 ${size.w} ${size.h}`} className="w-full h-full block">
              <g ref={gZoomRef}>
                {/* Capa actual coloreada */}
                {pathGen && visibleFeatures.map((f) => {
                  let code = "";
                  let name = "";
                  let levelLabel = "";
                  if (view.level === "dep") { code = depCode(f.properties); name = depName(f.properties); levelLabel = "Departamento"; }
                  else if (view.level === "prov") { code = provCode(f.properties); name = provName(f.properties); levelLabel = "Provincia"; }
                  else { code = distCode(f.properties); name = distName(f.properties); levelLabel = "Distrito"; }
                  const item = dataByUbigeo.get(code);
                  const fill = item ? (colorScale?.(item.placements) ?? "#e3e8ef") : "#e3e8ef";
                  const d = pathGen(f as unknown as GeoPermissibleObjects);
                  if (!d) return null;
                  return (
                    <path
                      key={code}
                      d={d}
                      fill={fill}
                      stroke="#fff"
                      strokeWidth={view.level === "dep" ? 1.1 : 0.5}
                      style={{ cursor: view.level === "dist" ? "default" : "pointer", vectorEffect: "non-scaling-stroke" }}
                      onMouseMove={(e) => showTooltip(e, item ?? { ubigeo: code, name, placements: 0, spending: 0 }, levelLabel)}
                      onMouseLeave={hideTooltip}
                      onClick={() => view.level !== "dist" && clickFeature(view.level, code)}
                    />
                  );
                })}
                {/* Contorno del scope encima */}
                {pathGen && scopeBorders.map((f, bi) => {
                  const d = pathGen(f as unknown as GeoPermissibleObjects);
                  if (!d) return null;
                  return (
                    <path
                      key={"b-" + bi}
                      d={d}
                      fill="none"
                      stroke="#4a5a70"
                      strokeWidth={1.4}
                      strokeOpacity={0.5}
                      style={{ vectorEffect: "non-scaling-stroke", pointerEvents: "none" }}
                    />
                  );
                })}
              </g>
            </svg>
          )}

          {/* Badge esquina derecha */}
          <div className="absolute right-3 top-3 z-10 text-[10px] tracking-widest font-bold text-[#8f0c20] bg-[#fdecef] border border-[#f6cdd5] rounded-full px-2 py-1">
            DATOS REALES · CAMPAÑA
          </div>
        </div>

        {/* SIDE PANEL */}
        <aside className="border-l border-ink-200 flex flex-col bg-white min-h-0">
          <div className="px-4 py-3 border-b border-ink-100">
            <div className="text-[10px] font-bold tracking-widest text-ink-500 uppercase font-mono">{levelKicker}</div>
            <div className="text-lg font-extrabold mt-0.5 leading-tight">{levelTitle}</div>
            <div className="text-xs text-ink-500 mt-0.5">{levelScope}</div>
          </div>

          {/* KPIs grid 2x2 */}
          <div className="grid grid-cols-2 border-b border-ink-100 divide-x divide-ink-100">
            <div className="px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-ink-500">Total inserciones</div>
              <div className="font-mono font-semibold text-lg mt-0.5">{fmtN(total)}</div>
            </div>
            <div className="px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-ink-500">Zonas con dato</div>
              <div className="font-mono font-semibold text-lg mt-0.5">{currentData.length}</div>
            </div>
            <div className="px-3 py-2 border-t border-ink-100">
              <div className="text-[10px] uppercase tracking-wide text-ink-500">Inversión total</div>
              <div className="font-mono font-semibold text-base mt-0.5">PEN {fmtN(sumSpending)}</div>
            </div>
            <div className="px-3 py-2 border-t border-ink-100">
              <div className="text-[10px] uppercase tracking-wide text-ink-500">Promedio / zona</div>
              <div className="font-mono font-semibold text-base mt-0.5">{fmtN(avg)}</div>
            </div>
          </div>

          {/* Leyenda */}
          <div className="px-4 py-3 border-b border-ink-100">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold">Densidad de inserciones</div>
              <div className="text-[10px] font-mono text-ink-500">CUANTILES</div>
            </div>
            <div className="space-y-1">
              {legendRows.length > 0 ? legendRows.map((r, i) => (
                <div key={i} className="flex items-center gap-2 py-0.5">
                  <span className="w-7 h-3.5 rounded-sm" style={{ backgroundColor: r.color, boxShadow: "inset 0 0 0 1px rgba(20,36,59,.08)" }} />
                  <span className="font-mono text-xs text-ink-700">{fmtN(r.lo)} – {fmtN(r.hi)}</span>
                  <span className="ml-auto font-mono text-[10px] text-ink-500">{r.count}</span>
                </div>
              )) : (
                <div className="flex items-center gap-2 py-0.5">
                  <span className="w-7 h-3.5 rounded-sm bg-[#e3e8ef]" />
                  <span className="text-xs text-ink-500">sin datos en este nivel</span>
                </div>
              )}
            </div>
          </div>

          {/* Ranking */}
          <div className="flex-1 overflow-y-auto p-1 min-h-0">
            <div className="flex items-center justify-between px-3 py-2">
              <div className="text-xs font-bold">{rankTitle} <span className="text-ink-400 font-normal">· {currentData.length}</span></div>
              <div className="text-[10px] font-semibold text-ink-500">▼ inserciones</div>
            </div>
            <div className="space-y-0.5">
              {currentData.slice().sort((a, b) => b.placements - a.placements).map((d, i) => {
                const maxV = Math.max(...currentData.map(x => x.placements)) || 1;
                const color = colorScale?.(d.placements) ?? "#2c73ac";
                const clickable = view.level !== "dist";
                return (
                  <button
                    key={d.ubigeo}
                    type="button"
                    disabled={!clickable}
                    onClick={() => {
                      if (view.level === "dep") clickFeature("dep", d.ubigeo);
                      else if (view.level === "prov") clickFeature("prov", d.ubigeo);
                    }}
                    onMouseEnter={() => {}}
                    className="w-full grid grid-cols-[22px_1fr_auto] items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-ink-50 disabled:cursor-default relative"
                  >
                    <span className="font-mono text-[10px] text-ink-500 text-right">{i + 1}</span>
                    <span className="text-xs font-semibold truncate">
                      <span className="inline-block w-2 h-2 rounded-sm mr-1.5 align-middle" style={{ backgroundColor: color }} />
                      {titleCase(d.name)}
                    </span>
                    <span className="font-mono text-xs font-semibold">{fmtN(d.placements)}</span>
                    <span className="absolute left-10 right-3 bottom-0.5 h-0.5 bg-ink-100 rounded overflow-hidden">
                      <span className="block h-full rounded" style={{ width: `${Math.max(3, 100 * d.placements / maxV)}%`, backgroundColor: color }} />
                    </span>
                  </button>
                );
              })}
              {!currentData.length && !(view.level === "prov" ? loadingProv : view.level === "dist" ? loadingDist : false) && (
                <div className="text-center py-6 text-xs text-ink-400">Sin datos en este nivel para los filtros aplicados</div>
              )}
              {((view.level === "prov" && loadingProv) || (view.level === "dist" && loadingDist)) && (
                <div className="text-center py-6 text-xs text-ink-500">Cargando datos…</div>
              )}
            </div>
          </div>

          <div className="border-t border-ink-100 px-4 py-2 text-[10px] text-ink-500 flex justify-between">
            <span>Geometría: <strong className="text-ink-700">INEI</strong> · UBIGEO</span>
            <span className="font-mono">Nivel {view.level.toUpperCase()}</span>
          </div>
        </aside>
      </div>

      {/* Tooltip flotante */}
      <div
        ref={tooltipRef}
        className="fixed pointer-events-none z-50 bg-[#0f2036] text-white rounded-lg px-3 py-2 min-w-[170px] shadow-2xl opacity-0 transition-opacity"
        style={{ fontSize: 12 }}
      >
        {hover && (
          <>
            <div className="text-[10px] tracking-widest uppercase font-bold text-[#8fb4dd]">{hover.kind}</div>
            <div className="font-bold text-[13px]">{hover.name}</div>
            <div className="font-mono text-base font-semibold mt-1">{fmtN(hover.value)} <span className="text-[10px] text-[#9fc2e6] font-normal">inserciones</span></div>
            <div className="text-[10px] text-[#aebfd2] mt-0.5">{hover.subtitle}</div>
            {view.level !== "dist" && (
              <div className="mt-1.5 pt-1.5 border-t border-white/15 text-[10px] text-[#9fc2e6]">
                Clic para {view.level === "dep" ? "ver provincias" : "ver distritos"}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
